/**
 * HarViewer Hook
 *
 * Downloads and displays HAR file requests with:
 * - Download progress bar
 * - On-demand parsing (lazy loading)
 * - LRU caching of parsed data with size limits
 * - Paginated request display
 * - Expandable request/response details
 * - Scrollable and resizable panels
 * - JSON formatting toggle
 *
 * Security features:
 * - Proper HTML escaping for both content and attributes
 * - HAR structure validation before parsing
 * - MIME type whitelisting for downloads
 * - Filename sanitization
 */

import { formatBytes } from "../utils/format-bytes";
import { LRUCache, megabytes } from "../utils/lru-cache";

const CACHE_MAX_FILES = 10;
const CACHE_MAX_MEMORY_MB = 50;
const DEFAULT_PAGE_SIZE = 5;
const PROGRESS_INDETERMINATE = 50;
const PROGRESS_COMPLETE = 100;
const HAR_VALIDATION_MAX_ENTRIES = 10000;
const PANEL_MIN_HEIGHT = 48;
const PANEL_MAX_HEIGHT = 600;
const FILENAME_MAX_LENGTH = 100;
const COPY_FEEDBACK_DURATION_MS = 1500;
const BODY_SIZE_INFO_THRESHOLD = 10000;
const BODY_DOWNLOAD_THRESHOLD = 1000;
const JSON_INDENT_SPACES = 2;
const HTTP_STATUS_SUCCESS_MIN = 200;
const HTTP_STATUS_SUCCESS_MAX = 300;
const HTTP_STATUS_REDIRECT_MAX = 400;
const HTTP_STATUS_CLIENT_ERROR_MAX = 500;

// Global LRU cache for parsed HAR data
const harCache = new LRUCache({
  maxEntries: CACHE_MAX_FILES,
  maxBytes: megabytes(CACHE_MAX_MEMORY_MB),
  label: "HAR file",
});

// Allowed MIME types for downloads (whitelist)
const ALLOWED_DOWNLOAD_MIMES = new Set([
  "application/json",
  "text/json",
  "application/xml",
  "text/xml",
  "text/html",
  "text/css",
  "text/javascript",
  "application/javascript",
  "text/plain",
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
  "image/svg+xml",
  "application/pdf",
  "application/octet-stream",
]);

// MIME to extension mapping
const MIME_TO_EXT = {
  "application/json": ".json",
  "text/json": ".json",
  "application/xml": ".xml",
  "text/xml": ".xml",
  "text/html": ".html",
  "text/css": ".css",
  "text/javascript": ".js",
  "application/javascript": ".js",
  "text/plain": ".txt",
  "image/png": ".png",
  "image/jpeg": ".jpg",
  "image/gif": ".gif",
  "image/webp": ".webp",
  "image/svg+xml": ".svg",
  "application/pdf": ".pdf",
  "application/octet-stream": ".bin",
};

export const HarViewer = {
  mounted() {
    this.archiveId = this.el.dataset.archiveId;
    this.downloadUrl = this.el.dataset.downloadUrl;
    this.pageSize = parseInt(this.el.dataset.pageSize || String(DEFAULT_PAGE_SIZE), 10);
    this.currentPage = 1;
    this.totalEntries = 0;
    this.harData = null;
    this.isLoading = false;
    this.isExpanded = false;

    // Track expanded entries and JSON format state
    this.expandedEntries = new Set();
    this.jsonFormatted = new Map();

    // DOM elements
    this.toggleBtn = this.el.querySelector("[data-toggle-btn]");
    this.contentContainer = this.el.querySelector("[data-content]");
    this.progressContainer = this.el.querySelector("[data-progress]");
    this.progressBar = this.el.querySelector("[data-progress-bar]");
    this.progressText = this.el.querySelector("[data-progress-text]");
    this.requestsContainer = this.el.querySelector("[data-requests]");
    this.paginationContainer = this.el.querySelector("[data-pagination]");
    this.errorContainer = this.el.querySelector("[data-error]");

    // Bind event handlers
    this.toggleBtn?.addEventListener("click", () => this.toggle());

    // Check cache first
    if (harCache.has(this.archiveId)) {
      this.harData = harCache.get(this.archiveId);
      this.totalEntries = this.harData.log?.entries?.length || 0;
    }
  },

  toggle() {
    if (this.isExpanded) {
      this.collapse();
    } else {
      this.expand();
    }
  },

  async expand() {
    this.isExpanded = true;
    this.contentContainer.classList.remove("hidden");
    this.toggleBtn.querySelector("[data-icon]")?.classList.add("rotate-180");

    if (this.harData) {
      this.renderCurrentPage();
    } else {
      await this.downloadAndParse();
    }
  },

  collapse() {
    this.isExpanded = false;
    this.contentContainer.classList.add("hidden");
    this.toggleBtn.querySelector("[data-icon]")?.classList.remove("rotate-180");
  },

  /**
   * Validates HAR file structure according to HAR 1.2 spec
   * @param {object} data - Parsed JSON data
   * @returns {{ valid: boolean, error?: string }}
   */
  validateHarStructure(data) {
    if (!data || typeof data !== "object") {
      return { valid: false, error: "Invalid HAR: not an object" };
    }

    if (!data.log || typeof data.log !== "object") {
      return { valid: false, error: "Invalid HAR: missing 'log' property" };
    }

    if (!data.log.version || typeof data.log.version !== "string") {
      return { valid: false, error: "Invalid HAR: missing or invalid 'log.version'" };
    }

    if (!Array.isArray(data.log.entries)) {
      return { valid: false, error: "Invalid HAR: 'log.entries' must be an array" };
    }

    // Validate each entry has required structure
    for (let i = 0; i < Math.min(data.log.entries.length, HAR_VALIDATION_MAX_ENTRIES); i++) {
      const entry = data.log.entries[i];
      if (!entry || typeof entry !== "object") {
        return { valid: false, error: `Invalid HAR: entry ${i} is not an object` };
      }
      if (!entry.request || typeof entry.request !== "object") {
        return { valid: false, error: `Invalid HAR: entry ${i} missing 'request'` };
      }
      if (!entry.response || typeof entry.response !== "object") {
        return { valid: false, error: `Invalid HAR: entry ${i} missing 'response'` };
      }
      if (typeof entry.request.url !== "string") {
        return { valid: false, error: `Invalid HAR: entry ${i} missing 'request.url'` };
      }
      if (typeof entry.request.method !== "string") {
        return { valid: false, error: `Invalid HAR: entry ${i} missing 'request.method'` };
      }
    }

    return { valid: true };
  },

  async downloadAndParse() {
    if (this.isLoading) return;
    this.isLoading = true;

    this.showProgress();
    this.hideError();

    try {
      const response = await this.fetchWithProgress(this.downloadUrl);
      const text = await response.text();

      this.updateProgress(PROGRESS_COMPLETE, "Parsing...");

      // Parse with error handling
      let parsedData;
      try {
        parsedData = JSON.parse(text);
      } catch (parseError) {
        throw new Error(`Failed to parse HAR file: ${parseError.message}`);
      }

      // Validate HAR structure
      const validation = this.validateHarStructure(parsedData);
      if (!validation.valid) {
        throw new Error(validation.error);
      }

      this.harData = parsedData;
      this.totalEntries = this.harData.log?.entries?.length || 0;

      // Cache it (LRU cache handles size limits)
      harCache.set(this.archiveId, this.harData);

      this.hideProgress();
      this.renderCurrentPage();
    } catch (error) {
      console.error("Failed to load HAR file:", error);
      this.hideProgress();
      this.showError(error.message || "Failed to load HAR file");
    } finally {
      this.isLoading = false;
    }
  },

  async fetchWithProgress(url) {
    const response = await fetch(url);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    const contentLength = response.headers.get("content-length");

    if (!contentLength || !response.body) {
      this.updateProgress(PROGRESS_INDETERMINATE, "Downloading...");
      return response;
    }

    const total = parseInt(contentLength, 10);
    let loaded = 0;

    // Use TransformStream for better browser compatibility
    const self = this;
    const progressStream = new TransformStream({
      transform(chunk, controller) {
        loaded += chunk.length;
        const percent = Math.round((loaded / total) * 100);
        self.updateProgress(
          percent,
          `Downloading... ${formatBytes(loaded)} / ${formatBytes(total)}`,
        );
        controller.enqueue(chunk);
      },
    });

    const newResponse = new Response(response.body.pipeThrough(progressStream), {
      status: response.status,
      statusText: response.statusText,
      headers: response.headers,
    });

    return newResponse;
  },

  showProgress() {
    this.progressContainer?.classList.remove("hidden");
    this.requestsContainer?.classList.add("hidden");
    this.paginationContainer?.classList.add("hidden");
  },

  hideProgress() {
    this.progressContainer?.classList.add("hidden");
  },

  updateProgress(percent, text) {
    if (this.progressBar) {
      this.progressBar.style.width = `${percent}%`;
    }
    if (this.progressText) {
      this.progressText.textContent = text || `${percent}%`;
    }
  },

  showError(message) {
    if (this.errorContainer) {
      this.errorContainer.classList.remove("hidden");
      // Use textContent for security (no HTML injection)
      this.errorContainer.textContent = message;
    }
  },

  hideError() {
    this.errorContainer?.classList.add("hidden");
  },

  renderCurrentPage() {
    if (!this.harData?.log?.entries) {
      this.requestsContainer.innerHTML = `
        <div class="text-center py-4 text-gray-500">
          No requests found in this HAR file
        </div>
      `;
      return;
    }

    const entries = this.harData.log.entries;
    const start = (this.currentPage - 1) * this.pageSize;
    const end = Math.min(start + this.pageSize, entries.length);
    const pageEntries = entries.slice(start, end);

    this.requestsContainer.classList.remove("hidden");
    this.requestsContainer.innerHTML = pageEntries
      .map((entry, idx) => this.renderEntry(entry, start + idx))
      .join("");

    this.bindEventHandlers();
    this.renderPagination();
  },

  /**
   * Selective DOM update for toggling entry expansion
   * Only updates the specific entry instead of re-rendering all
   */
  updateEntryExpansion(index) {
    const entryEl = this.requestsContainer.querySelector(`[data-entry-index="${index}"]`);
    if (!entryEl) {
      this.renderCurrentPage();
      return;
    }

    const detailsEl = entryEl.querySelector(`[data-details="${index}"]`);
    const chevronEl = entryEl.querySelector(`[data-chevron="${index}"]`);
    const isExpanded = this.expandedEntries.has(index);

    if (isExpanded) {
      detailsEl.classList.remove("hidden");
      chevronEl?.classList.add("rotate-180");

      // Render details content
      const entries = this.harData.log.entries;
      const entry = entries[index];
      if (entry) {
        detailsEl.innerHTML = this.renderEntryDetails(entry, index);
        this.bindEntryDetailHandlers(detailsEl);
      }
    } else {
      detailsEl.classList.add("hidden");
      chevronEl?.classList.remove("rotate-180");
      detailsEl.innerHTML = "";
    }
  },

  /**
   * Selective DOM update for JSON format toggle
   */
  updateBodyPanel(panelId) {
    // Find which entry this panel belongs to
    const match = panelId.match(/^(req|res)-body-(\d+)$/);
    if (!match) {
      this.renderCurrentPage();
      return;
    }

    const index = parseInt(match[2], 10);
    const entries = this.harData.log.entries;
    const entry = entries[index];
    if (!entry) return;

    const isRequest = match[1] === "req";
    const text = isRequest ? entry.request.postData?.text : entry.response.content?.text;
    const mimeType = isRequest
      ? entry.request.postData?.mimeType
      : entry.response.content?.mimeType;

    if (!text) return;

    const isJson = this.isJsonContent(mimeType, text);
    const isFormatted = this.jsonFormatted.get(panelId) ?? true;

    let displayText = text;
    if (isJson && isFormatted) {
      try {
        displayText = JSON.stringify(JSON.parse(text), null, JSON_INDENT_SPACES);
      } catch {
        displayText = text;
      }
    }

    // Update the pre element
    const preEl = this.requestsContainer.querySelector(`[data-body-text="${panelId}"]`);
    if (preEl) {
      preEl.textContent = displayText;
    }

    // Update the toggle button
    const toggleBtn = this.requestsContainer.querySelector(`[data-json-toggle="${panelId}"]`);
    if (toggleBtn) {
      toggleBtn.textContent = isFormatted ? "Formatted" : "Raw";
      if (isFormatted) {
        toggleBtn.classList.add("bg-indigo-50", "border-indigo-200", "text-indigo-700");
        toggleBtn.classList.remove("bg-white", "border-gray-200", "text-gray-600");
      } else {
        toggleBtn.classList.remove("bg-indigo-50", "border-indigo-200", "text-indigo-700");
        toggleBtn.classList.add("bg-white", "border-gray-200", "text-gray-600");
      }
    }
  },

  bindEventHandlers() {
    // Bind entry toggle events
    this.requestsContainer.querySelectorAll("[data-entry-toggle]").forEach((el) => {
      el.addEventListener("click", () => {
        const index = parseInt(el.dataset.entryToggle, 10);
        this.toggleEntry(index);
      });
    });

    // Bind handlers for already expanded entries
    this.requestsContainer.querySelectorAll("[data-details]").forEach((detailsEl) => {
      if (!detailsEl.classList.contains("hidden")) {
        this.bindEntryDetailHandlers(detailsEl);
      }
    });
  },

  bindEntryDetailHandlers(container) {
    // Bind JSON format toggle events
    container.querySelectorAll("[data-json-toggle]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        this.toggleJsonFormat(btn.dataset.jsonToggle);
      });
    });

    // Bind copy button events
    container.querySelectorAll("[data-copy]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const text = btn.dataset.copy;
        this.copyToClipboard(text, btn);
      });
    });

    // Bind body copy events
    container.querySelectorAll("[data-copy-body]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const panelId = btn.dataset.copyBody;
        const pre = container.querySelector(`[data-body-text="${panelId}"]`);
        if (pre) {
          this.copyToClipboard(pre.textContent, btn);
        }
      });
    });

    // Bind download button events
    container.querySelectorAll("[data-download-body]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        e.stopPropagation();
        const panelId = btn.dataset.downloadBody;
        const mimeType = btn.dataset.mime;
        const pre = container.querySelector(`[data-body-text="${panelId}"]`);
        if (pre) {
          this.downloadContent(pre.textContent, mimeType, panelId);
        }
      });
    });

    // Bind resize handles
    container.querySelectorAll("[data-resize]").forEach((handle) => {
      this.initResize(handle, container);
    });
  },

  async copyToClipboard(text, btn) {
    try {
      await navigator.clipboard.writeText(text);
      const originalHtml = btn.innerHTML;
      btn.innerHTML = `${this.checkIcon()} <span>Copied!</span>`;
      btn.classList.add("text-green-600", "border-green-200", "bg-green-50");
      setTimeout(() => {
        btn.innerHTML = originalHtml;
        btn.classList.remove("text-green-600", "border-green-200", "bg-green-50");
      }, COPY_FEEDBACK_DURATION_MS);
    } catch (err) {
      console.error("Failed to copy:", err);
    }
  },

  /**
   * Sanitizes filename to prevent path traversal and other attacks
   */
  sanitizeFilename(filename) {
    // Remove path separators and null bytes
    // eslint-disable-next-line no-control-regex
    let safe = filename.replace(/[/\\:\u0000]/g, "_");
    // Remove leading dots (hidden files / parent traversal)
    safe = safe.replace(/^\.+/, "");
    // Limit length
    if (safe.length > FILENAME_MAX_LENGTH) {
      safe = safe.substring(0, FILENAME_MAX_LENGTH);
    }
    // Ensure we have something
    return safe || "download";
  },

  /**
   * Validates and normalizes MIME type for downloads
   */
  validateMimeType(mimeType) {
    if (!mimeType || typeof mimeType !== "string") {
      return "application/octet-stream";
    }

    // Extract base MIME type (remove parameters like charset)
    const baseMime = mimeType.split(";")[0].trim().toLowerCase();

    // Check whitelist
    if (ALLOWED_DOWNLOAD_MIMES.has(baseMime)) {
      return baseMime;
    }

    // Check if it's a text type we should allow
    if (baseMime.startsWith("text/")) {
      return "text/plain";
    }

    // Default to octet-stream for unknown types
    return "application/octet-stream";
  },

  downloadContent(text, mimeType, panelId) {
    // Validate and sanitize MIME type
    const safeMime = this.validateMimeType(mimeType);

    // Get extension based on validated MIME type
    const ext = this.getFileExtension(safeMime);

    // Sanitize the filename
    const baseFilename = this.sanitizeFilename(`response-${panelId}`);
    const filename = `${baseFilename}${ext}`;

    const blob = new Blob([text], { type: safeMime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },

  checkIcon(sizeClass = "w-3 h-3") {
    return `<svg class="${sizeClass}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>`;
  },

  initResize(handle, container) {
    const panelId = handle.dataset.resize;
    const content = container.querySelector(`[data-panel-content="${panelId}"]`);
    if (!content) return;

    let startY, startHeight;

    const onMouseMove = (e) => {
      const delta = e.clientY - startY;
      const newHeight = Math.max(PANEL_MIN_HEIGHT, Math.min(PANEL_MAX_HEIGHT, startHeight + delta));
      content.style.maxHeight = `${newHeight}px`;
    };

    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
    };

    handle.addEventListener("mousedown", (e) => {
      e.preventDefault();
      startY = e.clientY;
      startHeight = content.offsetHeight;
      document.body.style.cursor = "ns-resize";
      document.body.style.userSelect = "none";
      document.addEventListener("mousemove", onMouseMove);
      document.addEventListener("mouseup", onMouseUp);
    });
  },

  renderEntry(entry, index) {
    const request = entry.request || {};
    const response = entry.response || {};
    let url;
    try {
      url = new URL(request.url || "");
    } catch {
      url = { pathname: request.url || "", host: "", search: "" };
    }
    const method = request.method || "GET";
    const status = response.status || 0;
    const time = Math.round(entry.time || 0);
    const size = response.content?.size || response.bodySize || 0;
    const error = response._error || "";
    const resourceType = entry._resourceType || "";

    const methodColor = this.getMethodColor(method);
    const statusBadge = this.getStatusBadge(status, error);
    const isExpanded = this.expandedEntries.has(index);

    return `
      <div class="border border-gray-200 rounded-lg hover:border-gray-300 transition-colors" data-entry-index="${index}">
        <div class="flex items-start justify-between gap-3 p-3 cursor-pointer select-none" data-entry-toggle="${index}">
          <div class="flex-1 min-w-0">
            <div class="flex items-center gap-2 mb-1">
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${methodColor}">
                ${this.escapeHtml(method)}
              </span>
              <span class="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusBadge.color}">
                ${this.escapeHtml(statusBadge.label)}
              </span>
              <span class="text-xs text-gray-500">${this.escapeHtml(String(time))}ms</span>
              <span class="text-xs text-gray-400">${this.escapeHtml(formatBytes(size))}</span>
              ${resourceType ? `<span class="text-xs text-gray-400">${this.escapeHtml(resourceType)}</span>` : ""}
            </div>
            <div class="text-sm font-medium text-gray-900 truncate" title="${this.escapeHtmlAttr(request.url || "")}">
              ${this.escapeHtml(url.pathname || "")}${url.search ? this.escapeHtml(url.search) : ""}
            </div>
            <div class="text-xs text-gray-500 truncate">
              ${this.escapeHtml(url.host || "")}
            </div>
            ${
              error
                ? `<div class="text-xs text-red-700 truncate font-mono" title="${this.escapeHtmlAttr(error)}">${this.escapeHtml(error)}</div>`
                : ""
            }
          </div>
          <button class="flex-shrink-0 text-gray-400 hover:text-gray-600 p-1 transition-transform ${isExpanded ? "rotate-180" : ""}" data-chevron="${index}">
            <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
            </svg>
          </button>
        </div>
        <div data-details="${index}" class="${isExpanded ? "" : "hidden"} border-t border-gray-100">
          ${isExpanded ? this.renderEntryDetails(entry, index) : ""}
        </div>
      </div>
    `;
  },

  renderEntryDetails(entry, index) {
    const request = entry.request || {};
    const response = entry.response || {};

    return `
      <div class="p-3 space-y-3 bg-gray-50/50">
        <!-- Failure -->
        ${this.renderFailure(response)}

        <!-- Full URL -->
        <div>
          <div class="flex items-center justify-between mb-1">
            <div class="text-xs font-medium text-gray-500">Full URL</div>
            <button class="copy-btn text-xs px-2 py-0.5 rounded border bg-white border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1" data-copy="${this.escapeHtmlAttr(request.url || "")}">
              ${this.copyIcon()}
              <span>Copy</span>
            </button>
          </div>
          <div class="text-xs text-gray-700 break-all bg-white p-2 rounded border border-gray-200 font-mono">
            ${this.escapeHtml(request.url || "")}
          </div>
        </div>

        <!-- Query Parameters -->
        ${this.renderQueryParams(request)}

        <!-- Request Headers -->
        ${this.renderHeadersPanel("Request Headers", request.headers, `req-headers-${index}`)}

        <!-- Request Body -->
        ${request.postData?.text ? this.renderBodyPanel("Request Body", request.postData.text, request.postData.mimeType, `req-body-${index}`) : ""}

        <!-- Response Headers -->
        ${this.renderHeadersPanel("Response Headers", response.headers, `res-headers-${index}`)}

        <!-- Response Body -->
        ${response.content?.text ? this.renderBodyPanel("Response Body", response.content.text, response.content.mimeType, `res-body-${index}`) : ""}

        <!-- Timing -->
        ${entry.timings ? this.renderTimings(entry.timings) : ""}
      </div>
    `;
  },

  renderFailure(response) {
    if (!response._error) return "";

    return `
      <div>
        <div class="text-xs font-medium text-gray-500 mb-1">Failure</div>
        <div class="text-xs text-red-800 break-all bg-red-50 p-2 rounded border border-red-200 font-mono">
          ${this.escapeHtml(response._error)}
        </div>
        ${
          response.comment
            ? `<div class="text-xs text-red-700 mt-1">${this.escapeHtml(response.comment)}</div>`
            : ""
        }
      </div>
    `;
  },

  renderQueryParams(request) {
    if (!request.queryString || request.queryString.length === 0) return "";

    const rows = request.queryString
      .map(
        (q) => `
        <tr class="border-b border-gray-100 last:border-0 group">
          <td class="py-1.5 p-2 pr-3 text-gray-600 font-medium whitespace-nowrap">${this.escapeHtml(q.name || "")}</td>
          <td class="py-1.5 text-gray-800 break-all">${this.escapeHtml(q.value || "")}</td>
          <td class="py-1.5 pl-2 w-8">
            <button class="copy-btn opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 p-0.5 rounded transition-opacity" data-copy="${this.escapeHtmlAttr(`${q.name || ""}=${q.value || ""}`)}" title="Copy parameter">
              ${this.copyIcon("w-3.5 h-3.5")}
            </button>
          </td>
        </tr>
      `,
      )
      .join("");

    return `
      <div>
        <div class="text-xs font-medium text-gray-500 mb-1">Query Parameters (${request.queryString.length})</div>
        <div class="bg-white rounded border border-gray-200 overflow-hidden">
          <table class="w-full text-xs font-mono">
            <tbody class="divide-y divide-gray-100">${rows}</tbody>
          </table>
        </div>
      </div>
    `;
  },

  renderHeadersPanel(title, headers, panelId) {
    if (!headers || headers.length === 0) return "";

    const rows = headers
      .map(
        (h) => `
        <tr class="border-b border-gray-100 last:border-0 group">
          <td class="py-1.5 pr-3 text-gray-600 font-medium whitespace-nowrap align-top">${this.escapeHtml(h.name || "")}</td>
          <td class="py-1.5 text-gray-800 break-all align-top">${this.escapeHtml(h.value || "")}</td>
          <td class="py-1.5 pl-2 w-8 align-top">
            <button class="copy-btn opacity-0 group-hover:opacity-100 text-gray-400 hover:text-gray-600 p-0.5 rounded transition-opacity" data-copy="${this.escapeHtmlAttr(`${h.name || ""}: ${h.value || ""}`)}" title="Copy header">
              ${this.copyIcon("w-3.5 h-3.5")}
            </button>
          </td>
        </tr>
      `,
      )
      .join("");

    return `
      <div>
        <div class="text-xs font-medium text-gray-500 mb-1">${this.escapeHtml(title)} (${headers.length})</div>
        <div class="bg-white rounded border border-gray-200 overflow-hidden resizable-panel" data-panel="${this.escapeHtmlAttr(panelId)}">
          <div class="max-h-48 overflow-auto p-2" data-panel-content="${this.escapeHtmlAttr(panelId)}">
            <table class="w-full text-xs font-mono">
              <tbody>${rows}</tbody>
            </table>
          </div>
          <div class="resize-handle h-1.5 bg-gray-100 hover:bg-indigo-200 cursor-ns-resize border-t border-gray-200" data-resize="${this.escapeHtmlAttr(panelId)}"></div>
        </div>
      </div>
    `;
  },

  renderBodyPanel(title, text, mimeType, panelId) {
    if (!text) return "";

    const isJson = this.isJsonContent(mimeType, text);
    const isXml = this.isXmlContent(mimeType, text);
    const isText = this.isTextContent(mimeType);
    const isBinary = !isText && !isJson && !isXml;
    const isFormatted = this.jsonFormatted.get(panelId) ?? true;
    let displayText = text;

    if (isJson && isFormatted) {
      try {
        displayText = JSON.stringify(JSON.parse(text), null, JSON_INDENT_SPACES);
      } catch {
        displayText = text;
      }
    }

    const sizeInfo = text.length > BODY_SIZE_INFO_THRESHOLD ? ` - ${formatBytes(text.length)}` : "";
    const canCopy = isText || isJson || isXml;
    const canDownload = isJson || isXml || isBinary || text.length > BODY_DOWNLOAD_THRESHOLD;
    const safeMimeAttr = this.escapeHtmlAttr(mimeType || "application/octet-stream");

    return `
      <div>
        <div class="flex items-center justify-between mb-1">
          <div class="text-xs font-medium text-gray-500">${this.escapeHtml(title)} (${this.escapeHtml(mimeType || "unknown")}${sizeInfo})</div>
          <div class="flex items-center gap-1">
            ${
              canCopy
                ? `
              <button class="copy-btn text-xs px-2 py-0.5 rounded border bg-white border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1" data-copy-body="${this.escapeHtmlAttr(panelId)}">
                ${this.copyIcon()}
                <span>Copy</span>
              </button>
            `
                : ""
            }
            ${
              canDownload
                ? `
              <button class="text-xs px-2 py-0.5 rounded border bg-white border-gray-200 text-gray-600 hover:bg-gray-50 transition-colors flex items-center gap-1" data-download-body="${this.escapeHtmlAttr(panelId)}" data-mime="${safeMimeAttr}">
                ${this.downloadIcon()}
                <span>Save</span>
              </button>
            `
                : ""
            }
            ${
              isJson
                ? `
              <button class="text-xs px-2 py-0.5 rounded border ${isFormatted ? "bg-indigo-50 border-indigo-200 text-indigo-700" : "bg-white border-gray-200 text-gray-600"} hover:bg-indigo-100 transition-colors" data-json-toggle="${this.escapeHtmlAttr(panelId)}">
                ${isFormatted ? "Formatted" : "Raw"}
              </button>
            `
                : ""
            }
          </div>
        </div>
        <div class="bg-white rounded border border-gray-200 overflow-hidden resizable-panel" data-panel="${this.escapeHtmlAttr(panelId)}">
          <div class="max-h-64 overflow-auto p-2" data-panel-content="${this.escapeHtmlAttr(panelId)}">
            <pre class="text-xs font-mono text-gray-800 whitespace-pre-wrap break-all" data-body-text="${this.escapeHtmlAttr(panelId)}">${this.escapeHtml(displayText)}</pre>
          </div>
          <div class="resize-handle h-1.5 bg-gray-100 hover:bg-indigo-200 cursor-ns-resize border-t border-gray-200" data-resize="${this.escapeHtmlAttr(panelId)}"></div>
        </div>
      </div>
    `;
  },

  isXmlContent(mimeType, text) {
    if (mimeType && (mimeType.includes("xml") || mimeType.includes("html"))) {
      return true;
    }
    if (text) {
      const trimmed = text.trim();
      return trimmed.startsWith("<?xml") || trimmed.startsWith("<!");
    }
    return false;
  },

  isTextContent(mimeType) {
    if (!mimeType) return true;
    return (
      mimeType.startsWith("text/") ||
      mimeType.includes("json") ||
      mimeType.includes("xml") ||
      mimeType.includes("javascript") ||
      mimeType.includes("html") ||
      mimeType.includes("css") ||
      mimeType.includes("plain")
    );
  },

  getFileExtension(mimeType) {
    if (!mimeType) return ".txt";

    for (const [mime, ext] of Object.entries(MIME_TO_EXT)) {
      if (mimeType === mime || mimeType.includes(mime.split("/")[1])) {
        return ext;
      }
    }
    return ".txt";
  },

  copyIcon(sizeClass = "w-3 h-3") {
    return `<svg class="${this.escapeHtmlAttr(sizeClass)}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"></path></svg>`;
  },

  downloadIcon(sizeClass = "w-3 h-3") {
    return `<svg class="${this.escapeHtmlAttr(sizeClass)}" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path></svg>`;
  },

  renderTimings(timings) {
    const items = [
      { label: "Blocked", value: timings.blocked, color: "bg-gray-400" },
      { label: "DNS", value: timings.dns, color: "bg-blue-400" },
      { label: "Connect", value: timings.connect, color: "bg-green-400" },
      { label: "SSL", value: timings.ssl, color: "bg-purple-400" },
      { label: "Send", value: timings.send, color: "bg-yellow-400" },
      { label: "Wait", value: timings.wait, color: "bg-orange-400" },
      { label: "Receive", value: timings.receive, color: "bg-red-400" },
    ].filter((item) => typeof item.value === "number" && item.value > 0);

    if (items.length === 0) return "";

    const total = items.reduce((sum, item) => sum + item.value, 0);

    return `
      <div>
        <div class="text-xs font-medium text-gray-500 mb-1">Timing (${Math.round(total)}ms total)</div>
        <div class="bg-white rounded border border-gray-200 p-2">
          <div class="flex h-2 rounded overflow-hidden mb-2">
            ${items.map((item) => `<div class="${item.color}" style="width: ${(item.value / total) * 100}%" title="${this.escapeHtmlAttr(`${item.label}: ${Math.round(item.value)}ms`)}"></div>`).join("")}
          </div>
          <div class="flex flex-wrap gap-x-3 gap-y-1 text-xs">
            ${items.map((item) => `<span class="flex items-center gap-1"><span class="w-2 h-2 rounded ${item.color}"></span>${this.escapeHtml(item.label)}: ${Math.round(item.value)}ms</span>`).join("")}
          </div>
        </div>
      </div>
    `;
  },

  isJsonContent(mimeType, text) {
    if (mimeType && (mimeType.includes("json") || mimeType.includes("javascript"))) {
      return true;
    }
    if (text) {
      const trimmed = text.trim();
      return (
        (trimmed.startsWith("{") && trimmed.endsWith("}")) ||
        (trimmed.startsWith("[") && trimmed.endsWith("]"))
      );
    }
    return false;
  },

  toggleEntry(index) {
    if (this.expandedEntries.has(index)) {
      this.expandedEntries.delete(index);
    } else {
      this.expandedEntries.add(index);
    }
    // Use selective DOM update instead of full re-render
    this.updateEntryExpansion(index);
  },

  toggleJsonFormat(panelId) {
    const current = this.jsonFormatted.get(panelId) ?? true;
    this.jsonFormatted.set(panelId, !current);
    // Use selective DOM update instead of full re-render
    this.updateBodyPanel(panelId);
  },

  renderPagination() {
    const totalPages = Math.ceil(this.totalEntries / this.pageSize);

    if (totalPages <= 1) {
      this.paginationContainer.classList.add("hidden");
      return;
    }

    this.paginationContainer.classList.remove("hidden");

    const start = (this.currentPage - 1) * this.pageSize + 1;
    const end = Math.min(this.currentPage * this.pageSize, this.totalEntries);

    this.paginationContainer.innerHTML = `
      <div class="flex items-center justify-between py-3 border-t border-gray-200">
        <div class="text-sm text-gray-600">
          <span class="font-medium">${start}–${end}</span> of <span class="font-medium">${this.totalEntries}</span> requests
        </div>
        <div class="flex items-center gap-1">
          ${
            this.currentPage > 1
              ? `
            <button data-page="${this.currentPage - 1}" class="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
              ← Prev
            </button>
          `
              : ""
          }
          <span class="px-3 py-1.5 text-sm font-medium text-indigo-600 bg-indigo-50 border border-indigo-200 rounded">
            ${this.currentPage}
          </span>
          ${
            this.currentPage < totalPages
              ? `
            <button data-page="${this.currentPage + 1}" class="px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50">
              Next →
            </button>
          `
              : ""
          }
        </div>
      </div>
      <div class="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
        <div class="bg-indigo-500 h-1.5 rounded-full transition-all" style="width: ${(this.currentPage / totalPages) * 100}%"></div>
      </div>
    `;

    // Bind pagination events
    this.paginationContainer.querySelectorAll("[data-page]").forEach((btn) => {
      btn.addEventListener("click", (e) => {
        this.currentPage = parseInt(e.target.dataset.page, 10);
        this.renderCurrentPage();
      });
    });
  },

  getMethodColor(method) {
    const colors = {
      GET: "bg-green-100 text-green-800",
      POST: "bg-blue-100 text-blue-800",
      PUT: "bg-yellow-100 text-yellow-800",
      PATCH: "bg-orange-100 text-orange-800",
      DELETE: "bg-red-100 text-red-800",
      OPTIONS: "bg-gray-100 text-gray-800",
      HEAD: "bg-purple-100 text-purple-800",
    };
    return colors[method] || "bg-gray-100 text-gray-800";
  },

  // A HAR marks failure with response._error, not with a status
  getStatusBadge(status, error) {
    if (!error) return { label: String(status), color: this.getStatusColor(status) };

    return { label: status ? String(status) : "failed", color: "bg-red-100 text-red-800" };
  },

  getStatusColor(status) {
    if (status >= HTTP_STATUS_SUCCESS_MIN && status < HTTP_STATUS_SUCCESS_MAX)
      return "bg-green-100 text-green-800";
    if (status >= HTTP_STATUS_SUCCESS_MAX && status < HTTP_STATUS_REDIRECT_MAX)
      return "bg-yellow-100 text-yellow-800";
    if (status >= HTTP_STATUS_REDIRECT_MAX && status < HTTP_STATUS_CLIENT_ERROR_MAX)
      return "bg-orange-100 text-orange-800";
    if (status >= HTTP_STATUS_CLIENT_ERROR_MAX) return "bg-red-100 text-red-800";
    return "bg-gray-100 text-gray-800";
  },

  /**
   * Escapes HTML for use in element content
   * Safe for text nodes but NOT for attributes
   */
  escapeHtml(text) {
    if (text === null || text === undefined) return "";
    if (typeof text !== "string") text = String(text);
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  },

  /**
   * Escapes HTML for use in attribute values
   * Handles quotes, ampersands, and other special characters
   */
  escapeHtmlAttr(text) {
    if (text === null || text === undefined) return "";
    if (typeof text !== "string") text = String(text);
    return text
      .replace(/&/g, "&amp;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\n/g, "&#10;")
      .replace(/\r/g, "&#13;");
  },

  destroyed() {
    // Cleanup event listeners if needed
    this.expandedEntries.clear();
    this.jsonFormatted.clear();
  },
};
